import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/formatters';
import ThreadPanel from '../components/ThreadPanel';

export default function Tasks() {
  const { user } = useAuth();
  const { taskId: urlTaskId } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ status: '', priority: '', assignee: '' });
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium' });
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const assigneeRef = useRef(null);
  const canCreate = user && ['admin', 'partner'].includes(user.role);

  useEffect(() => {
    function handleClickOutside(e) {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    }
    if (showUserDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await api.get('/api/tasks', { params: filters });
      setTasks(data.tasks);
    } catch (err) { console.error('Tasks fetch error:', err); }
  }, [filters]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    api.get('/api/users').then(({ data }) => setUsers(data.users)).catch(() => {});
  }, []);

  useEffect(() => {
    if (urlTaskId && tasks.length && !selected) {
      const task = tasks.find(t => t.task_number === parseInt(urlTaskId));
      if (task) setSelected(task);
    }
  }, [urlTaskId, tasks]);

  async function handleCreate(e) {
    e.preventDefault();
    await api.post('/api/tasks', { ...form, assignee_id: form.assignee_id ? parseInt(form.assignee_id) : null });
    setShowForm(false);
    setForm({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium' });
    setUserSearch('');
    fetchTasks();
  }

  async function updateStatus(taskId, status) {
    await api.put(`/api/tasks/${taskId}`, { status });
    fetchTasks();
    if (selected?.id === taskId) setSelected({ ...selected, status });
  }

  const priorityClass = (p) => `priority-${p}`;
  const todayET = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const isOverdue = (dueDate) => dueDate && dueDate < todayET;
  const isUrgent = (dueDate) => {
    if (!dueDate) return false;
    const daysLeft = (new Date(dueDate + 'T12:00:00') - new Date(todayET + 'T12:00:00')) / 86400000;
    return daysLeft >= 0 && daysLeft <= 1;
  };

  const filteredUsers = users.filter(u =>
    !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );
  const selectedUser = users.find(u => String(u.id) === String(form.assignee_id));

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Tasks</h2>
        {canCreate && <button className="btn btn-primary" onClick={() => setShowForm(true)}>New Task</button>}
      </div>

      <div className="filter-bar">
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="open">Open</option><option value="in_progress">In Progress</option><option value="complete">Complete</option>
        </select>
        <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
          <option value="">All Priorities</option>
          <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="detail-panel-header">
              <h3>New Task</h3>
              <button className="detail-panel-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1, position: 'relative' }} ref={assigneeRef}>
                  <label>Assignee</label>
                  <input type="text" placeholder="Search users..."
                    value={showUserDropdown ? userSearch : (selectedUser ? selectedUser.name : (form.assignee_id ? '' : ''))}
                    onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                    onFocus={() => setShowUserDropdown(true)}
                  />
                  {showUserDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '160px', overflowY: 'auto', zIndex: 50 }}>
                      <div onClick={() => { setForm({ ...form, assignee_id: '' }); setUserSearch(''); setShowUserDropdown(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-light)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                        Unassigned
                      </div>
                      {filteredUsers.map(u => (
                        <div key={u.id} onClick={() => { setForm({ ...form, assignee_id: String(u.id) }); setUserSearch(''); setShowUserDropdown(false); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                          <div style={{ fontWeight: 500 }}>{u.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{u.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ flex: 1 }}><label>Due Date</label><input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Priority</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-container sticky-col">
          <table>
            <thead><tr><th>#</th><th>Title</th><th>Assignee</th><th>Due</th><th>Priority</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id} className="clickable-row" onClick={() => { setSelected(t); navigate(`/tasks/${t.task_number}`, { replace: true }); }}>
                  <td style={{ color: 'var(--text-light)', fontSize: '12px' }}>{t.task_number ? `#${t.task_number}` : ''}</td>
                  <td><strong>{t.title}</strong></td>
                  <td>{t.assignee_name || 'Unassigned'}</td>
                  <td style={{ color: (isUrgent(t.due_date) || isOverdue(t.due_date)) ? 'var(--danger)' : undefined, fontWeight: (isUrgent(t.due_date) || isOverdue(t.due_date)) ? '600' : undefined }}>
                    {formatDate(t.due_date)}
                    {isOverdue(t.due_date) && t.status !== 'complete' ? ' (Overdue)' : isUrgent(t.due_date) ? ' (Due soon!)' : ''}
                  </td>
                  <td><span className={priorityClass(t.priority)} style={{ fontWeight: '600', textTransform: 'uppercase', fontSize: '11px' }}>{t.priority}</span></td>
                  <td><span className={`badge ${t.status === 'complete' ? 'badge-success' : t.status === 'in_progress' ? 'badge-info' : 'badge-default'}`}>{t.status}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    {t.status !== 'complete' && (
                      <select value={t.status} onChange={e => updateStatus(t.task_number || t.id, e.target.value)} style={{ width: 'auto', padding: '2px 4px', fontSize: '11px' }}>
                        <option value="open">Open</option><option value="in_progress">In Progress</option><option value="complete">Complete</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => { setSelected(null); navigate('/tasks', { replace: true }); }}>
          <div className="detail-panel" onClick={e => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h3>{selected.task_number ? `#${selected.task_number} ` : ''}{selected.title}</h3>
              <button className="detail-panel-close" onClick={() => { setSelected(null); navigate('/tasks', { replace: true }); }}>&times;</button>
            </div>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>{selected.description}</p>
            <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div><label>Assignee</label><div>{selected.assignee_name || 'Unassigned'}</div></div>
              <div><label>Created By</label><div>{selected.creator_name}</div></div>
              <div><label>Due Date</label><div>{formatDate(selected.due_date)}</div></div>
              <div><label>Priority</label><div className={priorityClass(selected.priority)}>{selected.priority}</div></div>
              <div><label>Status</label><div><span className={`badge ${selected.status === 'complete' ? 'badge-success' : 'badge-default'}`}>{selected.status}</span></div></div>
            </div>
            <ThreadPanel entityType="task" entityId={selected.id} />

            {(user && (['admin', 'partner'].includes(user.role) || selected.created_by === user.id)) && (
              <button className="btn btn-danger btn-sm" style={{ marginTop: '16px' }} onClick={async () => {
                  if (!window.confirm('Are you sure you want to delete this task?')) return;
                  await api.delete(`/api/tasks/${selected.task_number || selected.id}`);
                  setSelected(null);
                  navigate('/tasks', { replace: true });
                  fetchTasks();
                }}>Delete Task</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
